import React, { useEffect, useState, useCallback } from "react";
import { 
  AlertTriangle, 
  CheckCircle, 
  ChevronLeft, 
  ChevronRight,
  Trophy,
  Award,
  Code,
  Brain,
  BarChart3,
  Shield,
  Cloud,
  Smartphone,
  GitBranch,
  Palette,
  Gamepad2,
  Link,
  Database,
  Coffee,
  FileCode,
  Layout,
  Server,
  Box,
  Workflow,
  TerminalSquare,
  Cpu,
  Globe,
  Lock,
  Sparkles,
  Rocket,
  PenTool,
  Joystick,
  Hash,
  Users,
  HardDrive,
  Wifi,
  Key,
  Bug,
  Zap,
  Cctv,
  Fingerprint,
  Eye,
  Network,
  Laptop,
  Train,
  Binary,
  Hexagon,
  Cuboid,
  Star,
  Gift,
  ArrowLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import httpClient from "../Api/axios";

const QuizPage = () => {
  const navigate = useNavigate(); 
  const [step, setStep] = useState(1);
  const [domain, setDomain] = useState("");
  const [subDomain, setSubDomain] = useState("");
  const [level, setLevel] = useState("");
  const [count, setCount] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [tabChanged, setTabChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [userId, setUserId] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user ? (user.signUpId || user.id || "") : "";
    } catch {
      return "";
    }
  });
  const [userEmail, setUserEmail] = useState(localStorage.getItem("userEmail") || "");
  const [quizResult, setQuizResult] = useState(null);
  const [showCertificateOption, setShowCertificateOption] = useState(false);
  const [certificatePaid, setCertificatePaid] = useState(false);
  const [loadingRazorpay, setLoadingRazorpay] = useState(false);
  const [showTryAgainMessage, setShowTryAgainMessage] = useState(false);
  const [generatedCertificateUrl, setGeneratedCertificateUrl] = useState(null);
  const [isLoadingCompleted, setIsLoadingCompleted] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Track completed quizzes
  const [completedQuizzes, setCompletedQuizzes] = useState({});
  const [domainCertificates, setDomainCertificates] = useState({});
  
  const [showDomainCertificateModal, setShowDomainCertificateModal] = useState(false);
  const [completedDomain, setCompletedDomain] = useState("");
  const [domainCertificateUrl, setDomainCertificateUrl] = useState("");
  const [loadingDomainCertificate, setLoadingDomainCertificate] = useState(false);
  const [certificateOffered, setCertificateOffered] = useState(false);
  const [domainCheckDone, setDomainCheckDone] = useState({});
  
  // Add these new state variables for duplicate prevention
  const [isGeneratingDomainCert, setIsGeneratingDomainCert] = useState(false);
  const [processedDomains, setProcessedDomains] = useState(() => {
    const currentUserId = localStorage.getItem("userId");
    if (currentUserId) {
      const saved = localStorage.getItem(`processedDomains_${currentUserId}`);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {
          return {};
        }
      }
    }
    return {};
  });

  // Helper function to get current user ID
  const getCurrentUserId = useCallback(() => {
    const id = userId || localStorage.getItem("userId");
    return id;
  }, [userId]);

  // Helper function to get userName
  const getUserName = useCallback(() => {
    const storedUser = localStorage.getItem("user");
    
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        
        if (user.name && user.name !== "" && !user.name.includes("@")) {
          return user.name;
        }
        if (user.fullName && user.fullName !== "" && !user.fullName.includes("@")) {
          return user.fullName;
        }
        if (user.displayName && user.displayName !== "" && !user.displayName.includes("@")) {
          return user.displayName;
        }
        if (user.username && user.username !== "" && !user.username.includes("@")) {
          return user.username;
        }
        if (user.firstName && user.lastName) {
          return `${user.firstName} ${user.lastName}`.trim();
        }
        if (user.firstName) {
          return user.firstName;
        }
      } catch (e) {
        console.error("Error parsing user from localStorage:", e);
      }
    }
    
    const storedName = localStorage.getItem("userName");
    if (storedName && storedName !== "" && !storedName.includes("@")) {
      return storedName;
    }
    
    const email = userEmail || localStorage.getItem("userEmail");
    if (email && email.includes("@")) {
      const emailName = email.split("@")[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    
    return "Quiz User";
  }, [userEmail]);

  // Fetch user details from backend
  useEffect(() => {
    const fetchUserDetails = async () => {
      const currentUserId = getCurrentUserId();
      
      if (currentUserId) {
        try {
          const response = await httpClient.get(`/user/${currentUserId}`);
          
          if (response.data) {
            const existingUser = localStorage.getItem("user");
            let updatedUser = {};
            
            if (existingUser) {
              updatedUser = JSON.parse(existingUser);
            }
            
            updatedUser = {
              ...updatedUser,
              ...response.data,
              id: currentUserId
            };
            
            localStorage.setItem("user", JSON.stringify(updatedUser));
            
            const nameToStore = response.data.name || response.data.fullName || response.data.username || updatedUser.name;
            if (nameToStore && !nameToStore.includes("@")) {
              localStorage.setItem("userName", nameToStore);
            }
          }
        } catch (err) {
          console.error("Error fetching user details:", err);
        }
      }
    };
    
    fetchUserDetails();
  }, [getCurrentUserId]);

  // Load processed domains from localStorage on mount
  useEffect(() => {
    const currentUserId = getCurrentUserId();
    if (currentUserId) {
      const savedProcessed = localStorage.getItem(`processedDomains_${currentUserId}`);
      if (savedProcessed) {
        try {
          const parsed = JSON.parse(savedProcessed);
          setProcessedDomains(parsed);
          console.log("Loaded processed domains:", parsed);
        } catch (e) {}
      }
    }
  }, [getCurrentUserId]);

  // Load user's completed quizzes from backend - CRITICAL FIX
  useEffect(() => {
    const loadCompletedQuizzes = async () => {
      const currentUserId = getCurrentUserId();
      
      console.log("=== LOADING COMPLETED QUIZZES ===");
      console.log("User ID:", currentUserId);
      
      if (!currentUserId) {
        console.log("No user ID found, skipping load");
        setIsLoadingCompleted(false);
        setDataLoaded(true);
        return;
      }
      
      setIsLoadingCompleted(true);
      
      try {
        let completedMap = {};
        
        // First try: Get from QuizAttempt endpoint
        try {
          const response = await httpClient.get(`/QuizAttempt/user/${currentUserId}/completed`);
          console.log("QuizAttempt response:", response.data);
          
          if (response.data && Array.isArray(response.data)) {
            response.data.forEach(attempt => {
              if (attempt.quizSubDomain && attempt.percentage >= 60) {
                completedMap[attempt.quizSubDomain] = true;
              }
            });
          }
        } catch (quizErr) {
          console.log("QuizAttempt endpoint error:", quizErr.message);
        }
        
        // Second try: Get from Payment certificates endpoint (fallback)
        if (Object.keys(completedMap).length === 0) {
          try {
            const certResponse = await httpClient.get(`/payment/user/${currentUserId}/progress`);
            console.log("Payment progress response:", certResponse.data);
            
            if (certResponse.data && certResponse.data.completedQuizzes) {
              completedMap = certResponse.data.completedQuizzes;
            }
          } catch (certErr) {
            console.log("Payment progress endpoint error:", certErr.message);
          }
        }
        
        console.log("Final completed quizzes map:", completedMap);
        setCompletedQuizzes(completedMap);
        
        // Save to localStorage for caching
        if (Object.keys(completedMap).length > 0) {
          localStorage.setItem(`completedQuizzes_${currentUserId}`, JSON.stringify(completedMap));
        }
        
      } catch (err) {
        console.error("Error loading completed quizzes:", err);
        
        // Fallback to localStorage
        const savedCompleted = localStorage.getItem(`completedQuizzes_${currentUserId}`);
        if (savedCompleted) {
          try {
            const parsed = JSON.parse(savedCompleted);
            setCompletedQuizzes(parsed);
            console.log("Loaded from localStorage fallback:", parsed);
          } catch (e) {}
        }
      } finally {
        setIsLoadingCompleted(false);
        setDataLoaded(true);
      }
    };
    
    loadCompletedQuizzes();
  }, [getCurrentUserId]);

  // Save completed quizzes to localStorage whenever they change
  useEffect(() => {
    const currentUserId = getCurrentUserId();
    if (currentUserId && Object.keys(completedQuizzes).length > 0) {
      localStorage.setItem(`completedQuizzes_${currentUserId}`, JSON.stringify(completedQuizzes));
    }
  }, [completedQuizzes, getCurrentUserId]);

  // Check and award domain certificate - WITH DUPLICATE PREVENTION
  const checkAndAwardDomainCertificate = useCallback(async (domainName) => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) return;
    
    // Prevent multiple simultaneous calls
    if (isGeneratingDomainCert) {
      console.log("Already generating a certificate, skipping...");
      return;
    }
    
    // Check if already processed this domain
    if (processedDomains[domainName]) {
      console.log(`Domain certificate already processed for: ${domainName}`);
      return;
    }
    
    // Check if already have certificate in state
    if (domainCertificates[domainName]) {
      console.log(`Domain certificate already exists in state for: ${domainName}`);
      return;
    }
    
    const domainTopics = domains[domainName]?.subDomains || [];
    const allCompleted = domainTopics.every(topic => completedQuizzes[topic.name]);
    
    if (!allCompleted) {
      console.log(`Not all topics completed for domain: ${domainName}`);
      return;
    }
    
    // Mark as processing
    if (domainCheckDone[domainName]) {
      console.log(`Already checking domain: ${domainName}`);
      return;
    }
    
    setDomainCheckDone(prev => ({ ...prev, [domainName]: true }));
    setIsGeneratingDomainCert(true);
    
    // Mark as processed in localStorage to prevent duplicate on page reload
    const updatedProcessed = { ...processedDomains, [domainName]: true };
    setProcessedDomains(updatedProcessed);
    localStorage.setItem(`processedDomains_${currentUserId}`, JSON.stringify(updatedProcessed));
    localStorage.setItem(`domain_cert_processed_${currentUserId}_${domainName}`, 'true');
    
    setLoadingDomainCertificate(true);
    
    try {
      const userName = getUserName();
      console.log(`Requesting domain certificate for: ${domainName}`);
      
      const response = await httpClient.post("/payment/check-domain-certificate", {
        userId: currentUserId,
        userName: userName,
        userEmail: userEmail || localStorage.getItem("userEmail") || "",
        completedTopics: domainTopics.map(t => t.name)
      });
      
      console.log("Domain certificate response:", response.data);
      
      if (response.data.generated && response.data.certificates && response.data.certificates.length > 0) {
        const cert = response.data.certificates[0];
        setDomainCertificateUrl(cert.certificateUrl);
        setCompletedDomain(domainName);
        setShowDomainCertificateModal(true);
        
        const updatedDomainCerts = { ...domainCertificates, [domainName]: true };
        setDomainCertificates(updatedDomainCerts);
        localStorage.setItem(`domainCertificates_${currentUserId}`, JSON.stringify(updatedDomainCerts));
      } else {
        console.log("No new domain certificate generated - may already exist");
      }
    } catch (err) {
      console.error("Error generating domain certificate:", err);
    } finally {
      setLoadingDomainCertificate(false);
      setIsGeneratingDomainCert(false);
    }
  }, [getCurrentUserId, completedQuizzes, domainCertificates, userEmail, getUserName, domainCheckDone, processedDomains, isGeneratingDomainCert]);

  // Topic icons mapping
  const topicIcons = {
    "C Programming": <Cpu size={28} />,
    "Java": <Coffee size={28} />,
    "HTML": <Layout size={28} />,
    "CSS": <Layout size={28} />,
    "JavaScript": <Code size={28} />,
    "JSP": <Server size={28} />,
    "Servlet": <Server size={28} />,
    "ReactJS": <Code size={28} />,
    "Database (MySQL)": <Database size={28} />,
    "CRUD APIs": <Workflow size={28} />,
    "SpringBoot": <Coffee size={28} />,
    "Python": <FileCode size={28} />,
    "NumPy, Pandas": <BarChart3 size={28} />,
    "Machine Learning (Scikit-learn)": <Brain size={28} />,
    "Deep Learning (TensorFlow / PyTorch)": <Brain size={28} />,
    "Data Visualization (Matplotlib, Seaborn)": <BarChart3 size={28} />,
    "NLP basics": <Globe size={28} />,
    "Python / R": <FileCode size={28} />,
    "Excel Advanced": <BarChart3 size={28} />,
    "SQL": <Database size={28} />,
    "Power BI / Tableau": <BarChart3 size={28} />,
    "Data Cleaning": <Sparkles size={28} />,
    "Statistics basics": <BarChart3 size={28} />,
    "Networking Basics": <Network size={28} />,
    "Linux": <TerminalSquare size={28} />,
    "Ethical Hacking": <Shield size={28} />,
    "Cryptography basics": <Lock size={28} />,
    "Web Security (OWASP)": <Shield size={28} />,
    "Tools (Wireshark, Metasploit)": <Bug size={28} />,
    "Cloud Basics": <Cloud size={28} />,
    "AWS / Azure / GCP": <Cloud size={28} />,
    "Docker": <Box size={28} />,
    "Kubernetes": <Cuboid size={28} />,
    "CI/CD": <GitBranch size={28} />,
    "Serverless computing": <Zap size={28} />,
    "Java / Kotlin (Android)": <Smartphone size={28} />,
    "Flutter / Dart": <Smartphone size={28} />,
    "React Native": <Code size={28} />,
    "API Integration": <Workflow size={28} />,
    "UI/UX basics": <PenTool size={28} />,
    "Git & GitHub": <GitBranch size={28} />,
    "Jenkins": <Box size={28} />,
    "CI/CD pipelines": <GitBranch size={28} />,
    "Figma / Adobe XD": <PenTool size={28} />,
    "Wireframing": <PenTool size={28} />,
    "Prototyping": <PenTool size={28} />,
    "User Research": <Users size={28} />,
    "Design Principles": <Palette size={28} />,
    "C++": <FileCode size={28} />,
    "C#": <FileCode size={28} />,
    "Unity / Unreal Engine": <Gamepad2 size={28} />,
    "Game Physics": <Joystick size={28} />,
    "Animation basics": <Sparkles size={28} />,
    "Blockchain Basics": <Link size={28} />,
    "Solidity": <Hash size={28} />,
    "Smart Contracts": <Link size={28} />,
    "Ethereum": <Hexagon size={28} />,
    "Web3.js": <Globe size={28} />
  };

  const domainsList = [
    { name: "Full Stack Development", icon: <Code size={32} />, color: "#5a54eb" },
    { name: "AIML", icon: <Brain size={32} />, color: "#10b981" },
    { name: "Data Analytics / Data Science", icon: <BarChart3 size={32} />, color: "#f59e0b" },
    { name: "Cybersecurity", icon: <Shield size={32} />, color: "#ef4444" },
    { name: "Cloud Computing", icon: <Cloud size={32} />, color: "#3b82f6" },
    { name: "Mobile App Development", icon: <Smartphone size={32} />, color: "#8b5cf6" },
    { name: "DevOps Engineering", icon: <GitBranch size={32} />, color: "#06b6d4" },
    { name: "UIUX", icon: <Palette size={32} />, color: "#ec4899" },
    { name: "Game Development", icon: <Gamepad2 size={32} />, color: "#f43f5e" },
    { name: "Blockchain Development", icon: <Link size={32} />, color: "#14b8a6" }
  ];

  const domains = {
    "Full Stack Development": {
      icon: <Code size={32} />,
      color: "#5a54eb",
      subDomains: [
        { name: "C Programming", apiDomain: "C" },
        { name: "Java", apiDomain: "Java" },
        { name: "HTML", apiDomain: "HTML" },
        { name: "CSS", apiDomain: "CSS" },
        { name: "JavaScript", apiDomain: "JavaScript" },
        { name: "JSP", apiDomain: "JSP" },
        { name: "Servlet", apiDomain: "Servlet" },
        { name: "ReactJS", apiDomain: "ReactJS" },
        { name: "Database (MySQL)", apiDomain: "MySQL" },
        { name: "CRUD APIs", apiDomain: "CrudAPI" },
        { name: "SpringBoot", apiDomain: "SpringBoot" }
      ]
    },
    "AIML": {
      icon: <Brain size={32} />,
      color: "#10b981",
      subDomains: [
        { name: "Python", apiDomain: "Python" },
        { name: "NumPy, Pandas", apiDomain: "NumPy,Pandas" },
        { name: "Machine Learning (Scikit-learn)", apiDomain: "AIML" },
        { name: "Deep Learning (TensorFlow / PyTorch)", apiDomain: "DeepLearning" },
        { name: "Data Visualization (Matplotlib, Seaborn)", apiDomain: "DataVisualization" },
        { name: "NLP basics", apiDomain: "NLP" }
      ]
    },
    "Data Analytics / Data Science": {
      icon: <BarChart3 size={32} />,
      color: "#f59e0b",
      subDomains: [
        { name: "Python / R", apiDomain: "RPython" },
        { name: "Excel Advanced", apiDomain: "ExcelAdvanced" },
        { name: "SQL", apiDomain: "SQL" },
        { name: "Power BI / Tableau", apiDomain: "PowerBi" },
        { name: "Data Cleaning", apiDomain: "DataCleaning" },
        { name: "Statistics basics", apiDomain: "StatisticsBasics" }
      ]
    },
    "Cybersecurity": {
      icon: <Shield size={32} />,
      color: "#ef4444",
      subDomains: [
        { name: "Networking Basics", apiDomain: "NetworkingBasics" },
        { name: "Linux", apiDomain: "Linux" },
        { name: "Ethical Hacking", apiDomain: "EthicalHacking" },
        { name: "Cryptography basics", apiDomain: "CryptographyBasics" },
        { name: "Web Security (OWASP)", apiDomain: "WebSecurity" },
        { name: "Tools (Wireshark, Metasploit)", apiDomain: "WiresharkMetasploit" }
      ]
    },
    "Cloud Computing": {
      icon: <Cloud size={32} />,
      color: "#3b82f6",
      subDomains: [
        { name: "Cloud Basics", apiDomain: "WiresharkMetasploit" },
        { name: "AWS / Azure / GCP", apiDomain: "AWSAzureGCP" },
        { name: "Docker", apiDomain: "Docker" },
        { name: "Kubernetes", apiDomain: "Kubernetes" },
        { name: "CI/CD", apiDomain: "CICD" },
        { name: "Serverless computing", apiDomain: "ServerlessComputing" }
      ]
    },
    "Mobile App Development": {
      icon: <Smartphone size={32} />,
      color: "#8b5cf6",
      subDomains: [
        { name: "Java / Kotlin (Android)", apiDomain: "JavaKotlin" },
        { name: "Flutter / Dart", apiDomain: "FlutterDart" },
        { name: "React Native", apiDomain: "ReactNative" },
        { name: "API Integration", apiDomain: "APIIntegration" },
        { name: "UI/UX basics", apiDomain: "UIUX" }
      ]
    },
    "DevOps Engineering": {
      icon: <GitBranch size={32} />,
      color: "#06b6d4",
      subDomains: [
        { name: "Linux", apiDomain: "LinuxDevops" },
        { name: "Git & GitHub", apiDomain: "GitGithub" },
        { name: "Jenkins", apiDomain: "Jenkins" },
        { name: "Docker", apiDomain: "Docker" },
        { name: "Kubernetes", apiDomain: "Kubernetes" },
        { name: "CI/CD pipelines", apiDomain: "CICD" }
      ]
    },
    "UIUX": {
      icon: <Palette size={32} />,
      color: "#ec4899",
      subDomains: [
        { name: "Figma / Adobe XD", apiDomain: "Figma" },
        { name: "Wireframing", apiDomain: "Wireframing" },
        { name: "Prototyping", apiDomain: "Prototyping" },
        { name: "User Research", apiDomain: "UserResearch" },
        { name: "Design Principles", apiDomain: "DesignPrinciples" }
      ]
    },
    "Game Development": {
      icon: <Gamepad2 size={32} />,
      color: "#f43f5e",
      subDomains: [
        { name: "C++", apiDomain: "C++" },
        { name: "C#", apiDomain: "C#" },
        { name: "Unity / Unreal Engine", apiDomain: "UnityUnrealEngine" },
        { name: "Game Physics", apiDomain: "GamePhysics" },
        { name: "Animation basics", apiDomain: "Animation" }
      ]
    },
    "Blockchain Development": {
      icon: <Link size={32} />,
      color: "#14b8a6",
      subDomains: [
        { name: "Blockchain Basics", apiDomain: "BlockchainBasics" },
        { name: "Solidity", apiDomain: "Solidity" },
        { name: "Smart Contracts", apiDomain: "SmartContracts" },
        { name: "Ethereum", apiDomain: "SmartContracts" },
        { name: "Web3.js", apiDomain: "Web3" }
      ]
    }
  };

  // After quiz submission, update completed quizzes and check for domain certificate
  useEffect(() => {
    if (quizResult && quizResult.percentage && parseFloat(quizResult.percentage) >= 60 && subDomain && !certificateOffered && dataLoaded) {
      const currentUserId = getCurrentUserId();
      if (currentUserId) {
        const updatedCompleted = { ...completedQuizzes, [subDomain]: true };
        setCompletedQuizzes(updatedCompleted);
        localStorage.setItem(`completedQuizzes_${currentUserId}`, JSON.stringify(updatedCompleted));
        
        setShowCertificateOption(true);
        setCertificateOffered(true);
        
        // Check for domain certificate completion AFTER state is updated
        // Use setTimeout to ensure completedQuizzes state is updated
        setTimeout(() => {
          console.log("Checking domain completion for:", domain);
          checkAndAwardDomainCertificate(domain);
        }, 1500);
      }
    }
  }, [quizResult, subDomain, domain, completedQuizzes, getCurrentUserId, checkAndAwardDomainCertificate, certificateOffered, dataLoaded]);

  // Helper function to get grade details
  const getGradeDetails = (percentage) => {
    const percent = parseFloat(percentage);
    if (percent >= 91) {
      return { class: 'diamond', icon: '💎', name: 'Diamond - Ultimate Excellence' };
    } else if (percent >= 81) {
      return { class: 'platinum', icon: '⭐', name: 'Platinum - Elite Performance' };
    } else if (percent >= 71) {
      return { class: 'gold', icon: '🌟', name: 'Gold - Strong Achievement' };
    } else if (percent >= 60) {
      return { class: 'silver', icon: '✨', name: 'Silver - Solid Achievement' };
    }
    return { class: '', icon: '📜', name: 'Certificate of Completion' };
  };

  // Check authentication
  useEffect(() => {
    const checkAuth = () => {
      const email = localStorage.getItem("userEmail");
      const storedUserId = localStorage.getItem("userId");
      
      if (!email && !storedUserId) {
        alert("User not authenticated. Please log in again.");
        navigate("/");
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Load Razorpay script
  const loadRazorpayScript = useCallback(() => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        console.log('Razorpay SDK loaded successfully');
        resolve(true);
      };
      script.onerror = () => {
        console.error('Failed to load Razorpay SDK');
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }, []);

  const handleSubmit = useCallback(() => {
    if (submitted || isSubmitting) return;
    
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      alert("User not authenticated. Please log in again.");
      navigate("/");
      return;
    }
    
    setIsSubmitting(true);
    setSubmitted(true);
  
    const submission = {
      userId: currentUserId,
      quizTitle: `${subDomain} Quiz`,
      quizDescription: `Quiz on ${subDomain} at ${level} level`,
      quizDomain: domain,
      quizSubDomain: subDomain,
      quizLevel: level,
      quizType: "theory",
      quizDuration: count * 60,
      questionIds: questions.map(q => q.questionId),
      answers: answers,
      timeTaken: (count * 60) - timeLeft
    };
    
    console.log("Submitting quiz:", submission);

    httpClient
      .post("/QuizAttempt/submit", submission)
      .then((res) => {
        setQuizResult(res.data);
        setShowSuccessMessage(true);
        setIsSubmitting(false);
        console.log("Quiz submitted successfully:", res.data);
        
        const percentage = (res.data.score / res.data.totalQuestions) * 100;
        if (percentage >= 60) {
          setShowTryAgainMessage(false);
        } else {
          setShowCertificateOption(false);
          setShowTryAgainMessage(true);
        }
      })
      .catch((err) => {
        console.error("Error submitting quiz:", err);
        
        if (err.response) {
          if (err.response.status === 400) {
            alert(err.response.data || "Invalid submission data.");
          } else if (err.response.status === 401 || err.response.status === 404) {
            alert("User or questions not found. Please check your session.");
            navigate("/");
          } else if (err.response.status === 500) {
            alert("Server error. Please try again later.");
          } else {
            alert("Failed to submit quiz. Please try again.");
          }
        } else {
          alert("Network error. Please check your connection.");
        }
        
        setIsSubmitting(false);
        setSubmitted(false);
      });
  }, [submitted, isSubmitting, getCurrentUserId, domain, subDomain, level, count, questions, answers, timeLeft, navigate]);

  // Timer Effect
  useEffect(() => {
    if (step === 6 && timeLeft > 0 && !submitted && !tabChanged) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0 && step === 6 && questions.length > 0 && !submitted) {
      handleSubmit();
    }
  }, [timeLeft, step, submitted, tabChanged, handleSubmit, questions.length]);

  // Anti-Cheating Effect
  useEffect(() => {
    if (step === 6 && !submitted) {
      const handleVisibilityChange = () => {
        if (document.hidden) {
          setTabChanged(true); 
        }
      };
      
      window.addEventListener("blur", handleVisibilityChange);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        window.removeEventListener("blur", handleVisibilityChange);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    }
  }, [step, submitted]);

  // Fetch Questions
  useEffect(() => {
    if (step === 6 && !submitted && domain && subDomain && level && count > 0 && questions.length === 0) {
      const selectedDomain = domains[domain];
      const selectedTopic = selectedDomain?.subDomains.find(topic => topic.name === subDomain);
      let apiDomain = selectedTopic?.apiDomain || subDomain;
      
      const specialMappings = {
        "HTML": "HTML",
        "CSS": "CSS",
        "JavaScript": "JavaScript",
        "JSP": "JSP",
        "Servlet": "Servlet",
        "ReactJS": "ReactJS",
        "Database (MySQL)": "MySQL",
        "CRUD APIs": "CrudAPI",
        "SpringBoot": "SpringBoot",
        "C Programming": "C",
        "Java": "Java"
      };
      
      if (specialMappings[subDomain]) {
        apiDomain = specialMappings[subDomain];
      }
      
      console.log(`Fetching questions for: ${subDomain} using API domain: ${apiDomain}`);
      
      httpClient
        .get(`/Question/random?domain=${apiDomain}&level=${level}&count=${count}`)
        .then((res) => {
          if (res.data && res.data.length > 0) {
            setQuestions(res.data);
            setTimeLeft(count * 60);
          } else {
            alert(`No questions found for ${subDomain}. Please try another topic.`);
          }
        })
        .catch((err) => {
          console.error("Error fetching questions:", err);
          alert(`Failed to load questions for ${subDomain}. Please try again.`);
        });
    }
  }, [step, domain, subDomain, level, count, submitted, questions.length]);

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1 && !submitted) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0 && !submitted) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleAnswerChange = (questionId, selectedOption) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: selectedOption,
    }));
  };

  // UPDATED: Always return 199 for certificate amount (removed ₹1 option)
  const calculateCertificateAmount = () => {
    return 199;
  };

  const handleCertificatePayment = async () => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      alert("User not authenticated.");
      return;
    }

    setLoadingRazorpay(true);
    
    const isLoaded = await loadRazorpayScript();
    setLoadingRazorpay(false);
    
    if (!isLoaded) {
      alert("Failed to load payment gateway. Please check your internet connection and try again.");
      return;
    }

    const amount = calculateCertificateAmount();
    openRazorpay(amount);
  };

  const openRazorpay = (amount) => {
    if (!window.Razorpay) {
      alert("Razorpay SDK not loaded. Please try again.");
      return;
    }

    const currentUserId = getCurrentUserId();
    const currentUserEmail = userEmail || localStorage.getItem("userEmail") || "";
    const userName = getUserName();

    const percentageValue = quizResult 
      ? ((quizResult.score / quizResult.totalQuestions) * 100).toFixed(2)
      : null;
    
    const marksObtained = percentageValue ? `${percentageValue}%` : "N/A";

    const options = {
      key: "rzp_live_S1P5vjUnDHKtr4",
      amount: amount * 100,
      currency: "INR",
      name: "Quiz Certificate",
      description: `Certificate for ${subDomain} Quiz`,
      handler: function (response) {
        console.log("Payment Success:", response);
        
        const completePayload = {
          userId: currentUserId,
          quizId: questions?.length > 0 ? questions[0].quizId : "quiz_" + Date.now(),
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          userName: userName,
          domain: subDomain,
          amount: amount,
          quizTitle: `${subDomain} Quiz`,
          marksObtained: marksObtained,
          score: quizResult?.score || 0,
          totalQuestions: quizResult?.totalQuestions || 0,
          isDomainCertificate: false
        };
        
        console.log("Sending complete payload to backend:", completePayload);

        httpClient
          .post("/payment/verifyPayment", completePayload)
          .then((res) => {
            console.log("Payment verified successfully:", res.data);
            alert(`Payment Successful! Certificate generated.`);
            setCertificatePaid(true);

            if (res.data.certificateUrl) {
              setGeneratedCertificateUrl(res.data.certificateUrl);
            }
            
            // Refresh completed quizzes after payment
            const refreshCompleted = async () => {
              try {
                const refreshResponse = await httpClient.get(`/QuizAttempt/user/${currentUserId}/completed`);
                if (refreshResponse.data && Array.isArray(refreshResponse.data)) {
                  const completedMap = {};
                  refreshResponse.data.forEach(attempt => {
                    if (attempt.quizSubDomain && attempt.percentage >= 60) {
                      completedMap[attempt.quizSubDomain] = true;
                    }
                  });
                  setCompletedQuizzes(completedMap);
                  localStorage.setItem(`completedQuizzes_${currentUserId}`, JSON.stringify(completedMap));
                }
              } catch (err) {
                console.error("Error refreshing completed quizzes:", err);
              }
            };
            refreshCompleted();
          })
          .catch((err) => {
            console.error("Payment verification failed:", err);
            alert("Payment successful but certificate generation failed. Contact support.");
          });
      },
      prefill: {
        email: currentUserEmail,
        name: userName,
      },
      theme: {
        color: "#5a54eb",
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Error opening Razorpay:", error);
      alert("Failed to open payment gateway. Please try again.");
    }
  };

  const handleDomainCertificateDownload = () => {
    if (domainCertificateUrl) {
      const link = document.createElement('a');
      link.href = domainCertificateUrl;
      link.download = `${completedDomain}-domain-certificate.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setShowDomainCertificateModal(false);
  };

  const restartQuiz = () => {
    setStep(1);
    setDomain("");
    setSubDomain("");
    setLevel("");
    setCount(0);
    setQuestions([]);
    setAnswers({});
    setTimeLeft(0);
    setSubmitted(false);
    setCurrentQuestionIndex(0);
    setAcceptedRules(false);
    setTabChanged(false);
    setIsSubmitting(false);
    setShowSuccessMessage(false);
    setQuizResult(null);
    setShowCertificateOption(false);
    setCertificatePaid(false);
    setShowTryAgainMessage(false);
    setGeneratedCertificateUrl(null);
    setCertificateOffered(false);
    setIsGeneratingDomainCert(false);
    // Don't clear processedDomains or domainCertificates - they should persist
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCertificateDownload = (url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${subDomain}-certificate.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBackFromStep = (currentStep) => {
    switch(currentStep) {
      case 2:
        setStep(1);
        setDomain("");
        break;
      case 3:
        setStep(2);
        setSubDomain("");
        break;
      case 4:
        setStep(3);
        setLevel("");
        break;
      case 5:
        setStep(4);
        setCount(0);
        break;
      case 6:
        if (!submitted && window.confirm("Are you sure you want to go back? Your quiz progress will be lost.")) {
          restartQuiz();
        }
        break;
      default:
        setStep(1);
    }
  };

  const renderQuizResults = () => {
    if (!quizResult) return null;
    const { score, totalQuestions, percentage, results, questions: resultQuestions } = quizResult;
    const percentageValue = parseFloat(percentage);
    const qualifiesForCertificate = percentageValue >= 60;
    const gradeDetails = getGradeDetails(percentage);
    const userName = getUserName();
    
    return (
      <div className="results-container">
        <div className="results-header">
          <div className="trophy-wrapper">
            <Trophy size={64} className="trophy-icon" />
          </div>
          <h2>Congratulations!</h2>
          <p>You've completed the {subDomain} quiz</p>
        </div>
        
        <div className="score-display">
          <div className="score-circle">
            <span className="score-text">{score}/{totalQuestions}</span>
            <span className="score-percentage">{percentage}</span>
          </div>
        </div>
        
        <div className="results-details">
          <div className="result-item">
            <span className="label">Correct Answers:</span>
            <span className="value correct">{score}</span>
          </div>
          <div className="result-item">
            <span className="label">Incorrect Answers:</span>
            <span className="value incorrect">{totalQuestions - score}</span>
          </div>
          <div className="result-item">
            <span className="label">Total Questions:</span>
            <span className="value">{totalQuestions}</span>
          </div>
          <div className="result-item">
            <span className="label">Percentage:</span>
            <span className="value">{percentage}</span>
          </div>
        </div>
        
        <div className={`performance-grade ${gradeDetails.class}`}>
          <span className="grade-icon">{gradeDetails.icon}</span>
          <span>Performance Grade: {gradeDetails.name}</span>
        </div>
        
        {qualifiesForCertificate ? (
          <div className="qualification-message success">
            <CheckCircle size={24} />
            <p>Congratulations! You've scored {percentage}% which is above 60%.</p>
            <p>You are eligible to receive a certificate!</p>
          </div>
        ) : (
          <div className="qualification-message try-again">
            <AlertTriangle size={24} />
            <p>Sorry! You scored {percentage}% which is below 60%.</p>
            <p>Please try again to qualify for a certificate.</p>
          </div>
        )}
        
        <div className="question-review">
          <h3>Question Review</h3>
          {resultQuestions && resultQuestions.map((question, index) => (
            <div key={question.questionId || index} className="review-item">
              <div className="review-question">
                <span className="question-number">Q{index + 1}:</span>
                {question.question}
              </div>
              <div className="review-answer">
                <span className={`answer-status ${results && results[question.questionId] ? 'correct' : 'incorrect'}`}>
                  {results && results[question.questionId] ? (
                    <><CheckCircle size={16} /> Correct</>
                  ) : (
                    <><AlertTriangle size={16} /> Incorrect</>
                  )}
                </span>
                <div className="answer-details">
                  <p>Your answer: {answers[question.questionId] || 'Not answered'}</p>
                  <p>Correct answer: {question.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {qualifiesForCertificate && (
          certificatePaid && generatedCertificateUrl ? (
            <div className="certificate-section">
              <h3>Your Certificate</h3>
              <div className="certificate-display">
                <h4>🎉 Certificate Ready!</h4>
                <p>Congratulations, {userName}!</p>
                <p>You have successfully completed the {subDomain} quiz with {percentage}% score.</p>
                <button 
                  onClick={() => handleCertificateDownload(generatedCertificateUrl)}
                  className="nav-button certificate-button"
                >
                  Download / View Certificate
                </button>
              </div>
            </div>
          ) : showCertificateOption && !certificatePaid ? (
            <div className="certificate-payment">
              <h3>Get Your Certificate</h3>
              <div className="payment-options">
                <div className="payment-card">
                  <div className="payment-header">
                    <Award size={32} />
                    <h4>Standard Certificate</h4>
                  </div>
                  <div className="payment-details">
                    <p>Official digital certificate for {subDomain}</p>
                    <p>Valid for sharing and printing</p>
                    <p>Lifetime verification</p>
                  </div>       
                  <div className="payment-price">
                    ₹{calculateCertificateAmount()}
                  </div>
                  <button 
                    className="nav-button payment-button" 
                    onClick={handleCertificatePayment}
                    disabled={loadingRazorpay}
                  >
                    {loadingRazorpay ? "Loading Payment..." : "Get Certificate"}
                  </button>
                </div>
              </div>
              <button 
                className="nav-button secondary-button" 
                onClick={restartQuiz}
              >           
                Maybe later
              </button>
            </div>
          ) : null
        )}
        
        {!qualifiesForCertificate && (
          <div className="try-again-section">
            <button className="nav-button try-again-button" onClick={restartQuiz}>
              Try Again
            </button>
          </div>
        )}
      </div>
    );
  };

  // Calculate domain completion progress
  const getDomainProgress = (domainName) => {
    const domainTopics = domains[domainName]?.subDomains || [];
    const completedCount = domainTopics.filter(topic => completedQuizzes[topic.name]).length;
    return { completedCount, totalCount: domainTopics.length };
  };

  // Check if domain is fully completed
  const isDomainFullyCompleted = (domainName) => {
    const { completedCount, totalCount } = getDomainProgress(domainName);
    return completedCount === totalCount && totalCount > 0;
  };

  const firstRowDomains = domainsList.slice(0, 5);
  const secondRowDomains = domainsList.slice(5, 10);

  const currentTopics = domains[domain]?.subDomains || [];
  const { completedCount, totalCount } = getDomainProgress(domain);
  const isDomainCompleted = isDomainFullyCompleted(domain);
  const remainingCount = totalCount - completedCount;

  const showBackButton = step > 1 && step <= 6 && !(step === 6 && submitted);

  // Loading state
  if (isLoadingCompleted && step === 1) {
    return (
      <div className="quiz-container">
        <h1 className="quiz-header">🎯 Quiz Challenge</h1>
        <div className="loading" style={{ textAlign: "center", padding: "3rem" }}>
          <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>⏳</div>
          <p>Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container">
      <style>{`
        .quiz-container {
          max-width: 1200px;
          margin: 2rem auto;
          padding: 2rem;
          background: linear-gradient(135deg, #1a1a1d 0%, #2a2a2e 100%);
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
          min-height: 600px;
        }
        
        .quiz-header {
          text-align: center;
          margin-bottom: 2rem;
          background: linear-gradient(135deg, #5a54eb, #8b5cf6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-size: 2.5rem;
          font-weight: bold;
        }
        
        .quiz-step {
          text-align: center;
        }
        
        .quiz-step h2 {
          color: #e5e5e5;
          margin-bottom: 2rem;
          font-size: 1.8rem;
        }
        
        .domain-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 1.5rem;
          margin: 2rem 0;
        }
        
        .domain-card {
          background: linear-gradient(135deg, #2a2a2e 0%, #1f1f23 100%);
          border: 2px solid #3a3a3e;
          border-radius: 16px;
          padding: 1.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        
        .domain-card.completed {
          border-color: #4ade80;
          background: linear-gradient(135deg, rgba(74, 222, 128, 0.1) 0%, #1f1f23 100%);
        }
        
        .domain-card.completed::before {
          content: "✓";
          position: absolute;
          top: 10px;
          right: 10px;
          background: #4ade80;
          color: #1a1a1d;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: bold;
        }
        
        .domain-card:hover {
          transform: translateY(-5px);
          border-color: #5a54eb;
          box-shadow: 0 10px 30px rgba(90, 84, 235, 0.2);
        }
        
        .domain-card.selected {
          background: linear-gradient(135deg, #5a54eb 0%, #8b5cf6 100%);
          border-color: #6c66ff;
          color: white;
          transform: scale(1.02);
        }
        
        .progress-badge {
          margin-top: 0.75rem;
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 20px;
          background: rgba(74, 222, 128, 0.2);
          color: #4ade80;
          display: inline-block;
        }
        
        .domain-progress-banner {
          background: linear-gradient(135deg, rgba(90, 84, 235, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
          border: 1px solid #5a54eb;
          border-radius: 16px;
          padding: 1.25rem;
          margin-bottom: 2rem;
          text-align: center;
        }
        
        .domain-progress-banner.completed {
          background: linear-gradient(135deg, rgba(74, 222, 128, 0.15) 0%, rgba(74, 222, 128, 0.05) 100%);
          border-color: #4ade80;
        }
        
        .progress-title {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }
        
        .progress-title h3 {
          color: #e5e5e5;
          margin: 0;
          font-size: 1.1rem;
        }
        
        .progress-bar-container {
          width: 100%;
          height: 8px;
          background: #3a3a3e;
          border-radius: 10px;
          margin: 0.75rem 0;
          overflow: hidden;
        }
        
        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #5a54eb, #8b5cf6);
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        
        .progress-bar-fill.completed {
          background: linear-gradient(90deg, #4ade80, #22c55e);
        }
        
        .reward-message {
          margin-top: 0.75rem;
          padding: 0.5rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }
        
        .reward-message.active {
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
        }
        
        .reward-message.completed {
          background: rgba(74, 222, 128, 0.15);
          color: #4ade80;
        }
        
        .topic-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
          margin: 2rem 0;
        }
        
        .topic-card {
          background: linear-gradient(135deg, #2a2a2e 0%, #1f1f23 100%);
          border: 2px solid #3a3a3e;
          border-radius: 16px;
          padding: 1.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        
        .topic-card.completed {
          border-color: #4ade80;
          background: linear-gradient(135deg, rgba(74, 222, 128, 0.15) 0%, #1f1f23 100%);
        }
        
        .topic-card.completed::after {
          content: "✓";
          position: absolute;
          top: 8px;
          right: 8px;
          background: #4ade80;
          color: #1a1a1d;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
        }
        
        .topic-card:hover {
          transform: translateY(-5px);
          border-color: #5a54eb;
          box-shadow: 0 10px 30px rgba(90, 84, 235, 0.2);
        }
        
        .topic-card.selected {
          background: linear-gradient(135deg, #5a54eb 0%, #8b5cf6 100%);
          border-color: #6c66ff;
          color: white;
          transform: scale(1.02);
        }
        
        .topic-card.selected::after {
          background: white;
          color: #5a54eb;
        }
        
        .options-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          justify-content: center;
          margin: 2rem 0;
        }
        
        .domain-icon, .topic-icon {
          margin-bottom: 1rem;
          display: flex;
          justify-content: center;
        }
        
        .domain-icon svg, .topic-icon svg {
          stroke-width: 1.5;
          transition: all 0.3s ease;
        }
        
        .domain-card.selected .domain-icon svg,
        .topic-card.selected .topic-icon svg {
          color: white;
        }
        
        .domain-title {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
          color: #e5e5e5;
        }
        
        .topic-title {
          font-size: 0.9rem;
          font-weight: 500;
          margin: 0;
          color: #e5e5e5;
        }
        
        .domain-card.selected .domain-title,
        .topic-card.selected .topic-title {
          color: white;
        }
        
        .option-button {
          padding: 1rem 1.5rem;
          background: #2a2a2e;
          border: 2px solid #3a3a3e;
          border-radius: 12px;
          color: #e5e5e5;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 160px;
        }
        
        .option-button:hover {
          background: #3a3a3e;
          transform: translateY(-2px);
        }
        
        .option-button.selected {
          background: #5a54eb;
          border-color: #6c66ff;
          color: white;
        }
        
        .nav-button {
          padding: 0.75rem 1.5rem;
          background: #5a54eb;
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin: 0 0.5rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;
        }
        
        .nav-button:hover:not(:disabled) {
          background: #6c66ff;
          transform: translateY(-2px);
        }
        
        .nav-button:disabled {
          background: #3a3a3e;
          cursor: not-allowed;
          transform: none;
          opacity: 0.6;
        }
        
        .back-button {
          background: #3a3a3e;
        }
        
        .back-button:hover:not(:disabled) {
          background: #4a4a4e;
        }
        
        .timer {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ff6b6b;
          text-align: center;
          margin-bottom: 1rem;
          padding: 1rem;
          background: #2a2a2e;
          border-radius: 12px;
        }
        
        .question-container {
          background: #2a2a2e;
          padding: 1.5rem;
          border-radius: 16px;
          margin-bottom: 1.5rem;
        }
        
        .question-text {
          font-size: 1.2rem;
          color: #e5e5e5;
          margin-bottom: 1rem;
          line-height: 1.6;
        }
        
        .option-label {
          display: block;
          padding: 0.75rem;
          margin: 0.5rem 0;
          background: #3a3a3e;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .option-label:hover {
          background: #4a4a4e;
        }
        
        .option-input {
          margin-right: 0.75rem;
          cursor: pointer;
        }
        
        .option-input:disabled {
          cursor: not-allowed;
        }
        
        .question-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .question-counter {
          color: #a5a6ad;
          font-weight: 600;
        }
        
        .rules-container {
          background: #2a2a2e;
          padding: 1.5rem;
          border-radius: 16px;
          margin-bottom: 1.5rem;
          text-align: left;
        }
        
        .rules-title {
          color: #ff6b6b;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }
        
        .rules-list {
          list-style-type: none;
          padding: 0;
        }
        
        .rules-list li {
          margin-bottom: 0.75rem;
          padding-left: 1.5rem;
          position: relative;
          color: #e5e5e5;
        }
        
        .rules-list li:before {
          content: "•";
          color: #5a54eb;
          font-weight: bold;
          position: absolute;
          left: 0;
        }
        
        .warning-text {
          color: #ff6b6b;
          font-weight: bold;
          margin: 1rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .accept-checkbox {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 1.5rem 0;
          justify-content: center;
          color: #e5e5e5;
        }
        
        .checkbox-input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        
        .disqualified-message {
          text-align: center;
          color: #ff6b6b;
          margin-top: 2rem;
          padding: 2rem;
          background: #2a2a2e;
          border-radius: 16px;
        }
        
        .disqualified-message h2 {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        
        .restart-button {
          margin-top: 1.5rem;
          padding: 0.75rem 1.5rem;
          background: #5a54eb;
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .restart-button:hover {
          background: #6c66ff;
          transform: translateY(-2px);
        }

        .loading {
          text-align: center;
          color: #a5a6ad;
          font-size: 1.2rem;
          margin: 2rem 0;
        }
        
        .results-container {
          text-align: center;
          color: #e5e5e5;
        }
        
        .results-header {
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .trophy-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 1rem;
        }
        
        .trophy-icon {
          color: #fbbf24;
        }
        
        .results-header h2 {
          font-size: 2rem;
          color: #4ade80;
          margin: 0 0 0.5rem 0;
        }
        
        .score-display {
          margin: 2rem 0;
        }
        
        .score-circle {
          display: inline-flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 150px;
          height: 150px;
          border-radius: 50%;
          background: linear-gradient(135deg, #5a54eb, #6c66ff);
          box-shadow: 0 4px 20px rgba(92, 107, 192, 0.3);
        }
        
        .score-text {
          font-size: 2rem;
          font-weight: bold;
        }
        
        .score-percentage {
          font-size: 1.2rem;
        }
        
        .results-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin: 2rem 0;
        }
        
        .result-item {
          background: #2a2a2e;
          padding: 1rem;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
        }
        
        .result-item .label {
          font-size: 0.9rem;
          color: #a5a6ad;
          margin-bottom: 0.5rem;
        }
        
        .result-item .value {
          font-size: 1.2rem;
          font-weight: bold;
        }
        
        .result-item .correct {
          color: #4ade80;
        }
        
        .result-item .incorrect {
          color: #ff6b6b;
        }
        
        .performance-grade {
          margin: 1rem 0;
          padding: 1rem;
          border-radius: 12px;
          text-align: center;
          font-size: 1.2rem;
          font-weight: bold;
        }
        
        .performance-grade.diamond {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        
        .performance-grade.platinum {
          background: linear-gradient(135deg, #e0e0e0 0%, #b0b0b0 100%);
          color: #1a1a1d;
        }
        
        .performance-grade.gold {
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          color: #1a1a1d;
        }
        
        .performance-grade.silver {
          background: linear-gradient(135deg, #c0c0c0 0%, #a0a0a0 100%);
          color: #1a1a1d;
        }
        
        .grade-icon {
          font-size: 1.5rem;
          margin-right: 0.5rem;
          vertical-align: middle;
        }
        
        .question-review {
          text-align: left;
          margin: 2rem 0;
        }
        
        .question-review h3 {
          border-bottom: 2px solid #3a3a3e;
          padding-bottom: 0.5rem;
          margin-bottom: 1rem;
        }
        
        .review-item {
          background: #2a2a2e;
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        
        .review-question {
          margin-bottom: 1rem;
          font-weight: 500;
        }
        
        .question-number {
          color: #5a54eb;
          font-weight: bold;
          margin-right: 0.5rem;
        }
        
        .review-answer {
          padding-left: 1.5rem;
        }
        
        .answer-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
        }
        
        .answer-status.correct {
          color: #4ade80;
        }
        
        .answer-status.incorrect {
          color: #ff6b6b;
        }
        
        .answer-details p {
          margin: 0.25rem 0;
          font-size: 0.9rem;
        }
        
        .certificate-section {
          margin: 2rem 0;
        }
        
        .certificate-section h3 {
          color: #fbbf24;
          margin-bottom: 1rem;
          border-bottom: 2px solid #3a3a3e;
          padding-bottom: 0.5rem;
        }
        
        .certificate-display {
          padding: 1.5rem;
          background: #2a2a2e;
          border-radius: 12px;
          text-align: center;
        }
        
        .certificate-display h4 {
          color: #fbbf24;
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }
        
        .certificate-display p {
          margin: 0.5rem 0;
          color: #e5e5e5;
        }
        
        .certificate-button {
          margin-top: 1.5rem;
          background: #fbbf24;
          color: #1a1a1d;
        }
        
        .certificate-button:hover {
          background: #fcd34d;
        }
        
        .certificate-payment {
          margin: 2rem 0;
          padding: 1.5rem;
          background: #2a2a2e;
          border-radius: 12px;
        }
        
        .payment-options {
          display: flex;
          justify-content: center;
          margin: 1.5rem 0;
        }
        
        .payment-card {
          background: #3a3a3e;
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          max-width: 300px;
        }
        
        .payment-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 1rem;
        }
        
        .payment-header svg {
          color: #fbbf24;
          margin-bottom: 0.5rem;
        }
        
        .payment-details {
          margin: 1rem 0;
          text-align: left;
        }
        
        .payment-details p {
          margin: 0.5rem 0;
          padding-left: 1rem;
          position: relative;
        }
        
        .payment-details p:before {
          content: "✓";
          color: #4ade80;
          position: absolute;
          left: 0;
        }
        
        .payment-price {
          font-size: 1.5rem;
          font-weight: bold;
          color: #fbbf24;
          margin: 1rem 0;
        }
        
        .payment-button {
          background: #fbbf24;
          color: #1a1a1d;
          width: 100%;
        }
        
        .payment-button:hover:not(:disabled) {
          background: #fcd34d;
        }
        
        .secondary-button {
          background: #3a3a3e;
        }
        
        .secondary-button:hover:not(:disabled) {
          background: #4a4a4e;
        }
        
        .quiz-step h2 {
          color: #e5e5e5;
          margin-bottom: 1.5rem;
        }
        
        .quiz-step .options-grid button {
          min-height: 50px;
        }
        
        .question-nav .nav-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .qualification-message {
          padding: 1rem;
          border-radius: 12px;
          margin: 1rem 0;
          display: flex;
          align-items: center;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .qualification-message.success {
          background: rgba(74, 222, 128, 0.1);
          border: 1px solid #4ade80;
          color: #4ade80;
        }
        
        .qualification-message.try-again {
          background: rgba(255, 107, 107, 0.1);
          border: 1px solid #ff6b6b;
          color: #ff6b6b;
        }
        
        .qualification-message p {
          margin: 0;
        }
        
        .try-again-section {
          margin: 1rem 0;
          text-align: center;
        }
        
        .try-again-button {
          background: #ff6b6b;
        }
        
        .try-again-button:hover {
          background: #ff5252;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: linear-gradient(135deg, #2a2a2e 0%, #1f1f23 100%);
          border-radius: 20px;
          padding: 2rem;
          max-width: 400px;
          text-align: center;
          border: 2px solid #fbbf24;
        }
        
        .modal-content h3 {
          color: #fbbf24;
          margin-bottom: 1rem;
        }
        
        .modal-content p {
          color: #e5e5e5;
          margin-bottom: 1.5rem;
        }
        
        .button-group {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-top: 1rem;
          flex-wrap: wrap;
        }
        
        @media (max-width: 1024px) {
          .domain-grid {
            grid-template-columns: repeat(3, 1fr);
          }
          .topic-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        
        @media (max-width: 768px) {
          .quiz-container {
            margin: 1rem;
            padding: 1rem;
          }
          
          .domain-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }
          
          .topic-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }
          
          .domain-title, .topic-title {
            font-size: 0.85rem;
          }
          
          .options-grid {
            flex-direction: column;
            align-items: center;
          }
          
          .option-button {
            width: 100%;
            max-width: 250px;
          }
          
          .question-nav {
            flex-direction: column;
          }
        }
        
        @media (max-width: 480px) {
          .domain-grid {
            grid-template-columns: 1fr;
          }
          .topic-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      
      {showDomainCertificateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <Trophy size={48} color="#fbbf24" style={{ marginBottom: "1rem" }} />
            <h3>🏆 Domain Mastery Unlocked! 🏆</h3>
            <p>Congratulations! You have completed all quizzes in <strong>{completedDomain}</strong>!</p>
            <p>You have earned the Domain Mastery Certificate.</p>
            <button className="nav-button" onClick={handleDomainCertificateDownload}>
              Download Domain Certificate
            </button>
            <button className="nav-button secondary-button" onClick={() => setShowDomainCertificateModal(false)} style={{ marginTop: "0.5rem" }}>
              Close
            </button>
          </div>
        </div>
      )}
      
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        {showBackButton && (
          <button 
            className="nav-button back-button" 
            onClick={() => handleBackFromStep(step)}
          >
            <ArrowLeft size={20} /> Back
          </button>
        )}
        <div style={{ flex: 1 }} />
      </div>
      
      <h1 className="quiz-header">🎯 Quiz Challenge</h1>
      
      {step === 1 && (
        <div className="quiz-step">
          <h2>🚀 Choose Your Domain</h2>
          <div className="domain-grid">
            {firstRowDomains.map((tech) => {
              const { completedCount, totalCount } = getDomainProgress(tech.name);
              const isCompleted = isDomainFullyCompleted(tech.name);
              return (
                <div
                  key={tech.name}
                  className={`domain-card ${domain === tech.name ? "selected" : ""} ${isCompleted ? "completed" : ""}`}
                  onClick={() => {
                    setDomain(tech.name);
                    setSubDomain("");
                  }}
                >
                  <div className="domain-icon" style={{ color: tech.color }}>
                    {tech.icon}
                  </div>
                  <p className="domain-title">{tech.name}</p>
                  <div className="progress-badge">
                    {completedCount}/{totalCount} Completed
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="domain-grid">
            {secondRowDomains.map((tech) => {
              const { completedCount, totalCount } = getDomainProgress(tech.name);
              const isCompleted = isDomainFullyCompleted(tech.name);
              return (
                <div
                  key={tech.name}
                  className={`domain-card ${domain === tech.name ? "selected" : ""} ${isCompleted ? "completed" : ""}`}
                  onClick={() => {
                    setDomain(tech.name);
                    setSubDomain("");
                  }}
                >
                  <div className="domain-icon" style={{ color: tech.color }}>
                    {tech.icon}
                  </div>
                  <p className="domain-title">{tech.name}</p>
                  <div className="progress-badge">
                    {completedCount}/{totalCount} Completed
                  </div>
                </div>
              );
            })}
          </div>
          
          {domain && (
            <div style={{ marginTop: "2rem" }}>
              <button className="nav-button" onClick={() => setStep(2)}>
                Next →
              </button>
            </div>
          )}
        </div>
      )}
      
      {step === 2 && domain && (
        <div className="quiz-step">
          <h2>📚 Choose {domain} Topic</h2>
          
          <div className={`domain-progress-banner ${isDomainCompleted ? "completed" : ""}`}>
            <div className="progress-title">
              <Trophy size={20} color={isDomainCompleted ? "#4ade80" : "#fbbf24"} />
              <h3>{domain} Progress</h3>
            </div>
            <div className="progress-bar-container">
              <div 
                className={`progress-bar-fill ${isDomainCompleted ? "completed" : ""}`}
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              />
            </div>
            <p style={{ color: "#a5a6ad", margin: "0.5rem 0 0 0" }}>
              {completedCount} of {totalCount} topics completed
            </p>
            {!isDomainCompleted && remainingCount > 0 && (
              <div className="reward-message active">
                <Gift size={18} />
                <span>🎁 Complete all {totalCount} topics to get a FREE {domain} Mastery Certificate!</span>
              </div>
            )}
            {isDomainCompleted && (
              <div className="reward-message completed">
                <Trophy size={18} />
                <span>🏆 Congratulations! You've earned the {domain} Mastery Certificate! 🏆</span>
              </div>
            )}
          </div>
          
          <div className="topic-grid">
            {currentTopics.map((topic) => (
              <div
                key={topic.name}
                className={`topic-card ${subDomain === topic.name ? "selected" : ""} ${completedQuizzes[topic.name] ? "completed" : ""}`}
                onClick={() => setSubDomain(topic.name)}
              >
                <div className="topic-icon" style={{ color: domains[domain]?.color }}>
                  {topicIcons[topic.name] || <FileCode size={28} />}
                </div>
                <p className="topic-title">{topic.name}</p>
                {completedQuizzes[topic.name] && (
                  <div style={{ fontSize: "0.7rem", marginTop: "0.5rem", color: "#4ade80" }}>
                    ✓ Completed
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {subDomain && (
            <div style={{ marginTop: "2rem" }}>
              <button className="nav-button" onClick={() => setStep(3)}>
                Next →
              </button>
            </div>
          )}
        </div>
      )}
      
      {step === 3 && (
        <div className="quiz-step">
          <h2>⚡ Choose Difficulty Level</h2>
          <div className="options-grid">
            <button
              className={`option-button ${level === "easy" ? "selected" : ""}`}
              onClick={() => setLevel("easy")}
            >
              🟢 Easy
            </button>
            <button
              className={`option-button ${level === "medium" ? "selected" : ""}`}
              onClick={() => setLevel("medium")}
            >
              🟡 Medium
            </button>
            <button
              className={`option-button ${level === "hard" ? "selected" : ""}`}
              onClick={() => setLevel("hard")}
            >
              🔴 Hard
            </button>
          </div>
          {level && (
            <div style={{ marginTop: "2rem" }}>
              <button className="nav-button" onClick={() => setStep(4)}>
                Next →
              </button>
            </div>
          )}
        </div>
      )}
      
      {step === 4 && (
        <div className="quiz-step">
          <h2>❓ Choose Number of Questions</h2>
          <div className="options-grid">
            {/* UPDATED: Only show 20 Questions option, removed 1 Question */}
            <button
              className={`option-button ${count === 20 ? "selected" : ""}`}
              onClick={() => setCount(20)}
            >
              20 Questions (₹199)
            </button>
          </div>
          {count > 0 && (
            <div style={{ marginTop: "2rem" }}>
              <button className="nav-button" onClick={() => setStep(5)}>
                Next →
              </button>
            </div>
          )}
        </div>
      )}
      
      {step === 5 && (
        <div className="quiz-step">
          <h2>📋 Rules & Regulations</h2>
          <div className="rules-container">
            <h3 className="rules-title">
              <AlertTriangle size={24} /> Important Instructions
            </h3>
            <ul className="rules-list">
              <li>You have {count} questions to answer in {formatTime(count * 60)}.</li>
              <li>Each question has only one correct answer.</li>
              <li>There is no negative marking for wrong answers.</li>
              <li>Answers are saved automatically as you select them.</li>
              <li>You can navigate between questions using Previous and Next buttons.</li>
              <li>You can change your answers anytime during the quiz.</li>
            </ul>
            <div className="warning-text">
              <AlertTriangle size={18} /> WARNING: Do not switch tabs or open another window during the quiz.
            </div>
            <ul className="rules-list">
              <li>If you switch to another tab, you will be immediately disqualified.</li>
              <li>If you open another application or window, you will be disqualified.</li>
              <li>If you try to refresh the page, you will be disqualified.</li>
            </ul>
          </div>
          <div className="accept-checkbox">
            <input
              type="checkbox"
              id="acceptRules"
              className="checkbox-input"
              checked={acceptedRules}
              onChange={() => setAcceptedRules(!acceptedRules)}
              disabled={submitted}
            />
            <label htmlFor="acceptRules"> I have read and understood all the rules and regulations </label>
          </div>
          {acceptedRules && !submitted && (
            <button className="nav-button" onClick={() => setStep(6)}>
              🚀 Start Quiz
            </button>
          )}
        </div>
      )}
      
      {step === 6 && (
        <div>
          {questions.length === 0 ? (
            <div className="loading">Loading questions...</div>
          ) : tabChanged && !submitted ? (
            <div className="disqualified-message">
              <h2>
                <AlertTriangle size={32} /> You have been disqualified!
              </h2>
              <p>You switched tabs or opened another window during the quiz.</p>
              <button className="restart-button" onClick={restartQuiz}>
                Start Over
              </button>
            </div>
          ) : submitted && showSuccessMessage && quizResult ? (
            renderQuizResults()
          ) : (
            <>
              <div className="timer">
                ⏱️ Time Left: {formatTime(timeLeft)}
              </div>
              <div className="question-container">
                <p className="question-text">
                  <b>
                    {currentQuestionIndex + 1}. {questions[currentQuestionIndex]?.question}
                  </b>
                </p>
                {questions[currentQuestionIndex]?.options && questions[currentQuestionIndex].options.map((opt, i) => (
                  <label key={i} className="option-label">
                    <input
                      type="radio"
                      className="option-input"
                      name={questions[currentQuestionIndex].questionId}
                      value={opt}
                      checked={answers[questions[currentQuestionIndex].questionId] === opt}
                      onChange={() => handleAnswerChange(questions[currentQuestionIndex].questionId, opt)}
                      disabled={submitted}
                    />
                    {opt}
                  </label>
                ))}
              </div>
              <div className="question-nav">
                <button
                  className="nav-button"
                  onClick={handlePrevQuestion}
                  disabled={currentQuestionIndex === 0 || submitted}
                >
                  <ChevronLeft size={20} /> Previous
                </button>
                <span className="question-counter">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </span>
                {currentQuestionIndex < questions.length - 1 ? (
                  <button
                    className="nav-button"
                    onClick={handleNextQuestion}
                    disabled={submitted}
                  >
                    Next <ChevronRight size={20} />
                  </button>
                ) : (
                  <button
                    className="nav-button"
                    onClick={handleSubmit}
                    disabled={submitted || isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "📤 Submit Quiz"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizPage;